import React, { useEffect, useState } from 'react';
import { FaEye, FaEdit, FaBan, FaDownload, FaPlus, FaSort, FaSortUp, FaSortDown, FaCheck } from 'react-icons/fa';
import dayjs from 'dayjs';
import CurrencyFormModal from './CurrencyFormModal';
import CurrencyViewModal from './CurrencyViewModal';
import Sidebar from './Sidebar';
import { useNavigate } from 'react-router-dom';

const API_URL = '/api';

const CurrencyMaster = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCurrencies();
  }, []);

  // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchCurrencies = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/currencies`);
      const data = await res.json();
      setCurrencies(data);
    } catch {
      setCurrencies([]);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedCurrencies = [...currencies].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      
      // Handle date fields
      if (key === 'LastUpdated' || key === 'EffectiveDate') {
        return direction === 'asc' 
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      
      // Handle numeric fields
      if (key === 'CurrencyCode') {
        const aNum = parseInt(a[key].split('-')[1]);
        const bNum = parseInt(b[key].split('-')[1]);
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string fields
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setCurrencies(sortedCurrencies);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ml-1" /> 
      : <FaSortDown className="ml-1" />;
  };

  const handleView = (currency) => {
    setSelectedCurrency(currency);
    setShowView(true);
  };

  const handleEdit = (currency) => {
    setSelectedCurrency(currency);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleDeactivate = async (currency) => {
    if (!window.confirm('Are you sure you want to deactivate this currency?')) return;
    
    try {
      const response = await fetch(`${API_URL}/currencies/${currency.CurrencyCode}/deactivate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to deactivate currency');
      }
      
      fetchCurrencies();
    } catch (error) {
      console.error('Error deactivating currency:', error);
      alert(error.message || 'An error occurred while deactivating the currency');
    }
  };

  const handleReactivate = async (currency) => {
    if (!window.confirm('Are you sure you want to reactivate this currency?')) return;
    
    try {
      const response = await fetch(`${API_URL}/currencies/${currency.CurrencyCode}/reactivate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reactivate currency');
      }
      
      fetchCurrencies();
    } catch (error) {
      console.error('Error reactivating currency:', error);
      alert(error.message || 'An error occurred while reactivating the currency');
    }
  };

  const handleDownloadCSV = () => {
    const headers = [
      'CurrencyCode', 'CurrencyName', 'Symbol', 'DefaultRate', 'Status', 'EffectiveDate', 'LastUpdated'
    ];
    const rows = currencies.map(c => [
      c.CurrencyCode, c.CurrencyName, c.Symbol, c.DefaultRate, c.Status, 
      dayjs(c.EffectiveDate).format('YYYY-MM-DD'), dayjs(c.LastUpdated).format('YYYY-MM-DD HH:mm')
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'currencies.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreate = () => {
    setSelectedCurrency(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (isEdit && selectedCurrency) {
        const response = await fetch(`${API_URL}/currencies/${selectedCurrency.CurrencyCode}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update currency');
        }
      } else {
        const response = await fetch(`${API_URL}/currencies`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create currency');
        }
      }
      setShowForm(false);
      fetchCurrencies();
    } catch (error) {
      console.error('Error saving currency:', error);
      alert(error.message || 'An error occurred while saving the currency');
    } finally {
      setFormLoading(false);
    }
  };

  const getStatusColor = (status) => {
    // Handle boolean, string, and numeric status values
    const isActive = status === true || status === 'Active' || status === 1 || status === '1';
    return isActive ? 'bg-[#52ab98] text-white' : 'bg-gray-300 text-gray-500';
  };

  const getStatusText = (status) => {
    // Convert boolean, numeric or string status to text
    if (status === true || status === 1 || status === '1') return 'Active';
    if (status === false || status === 0 || status === '0') return 'Inactive';
    return status || 'Unknown';
  };

  const handleLogout = () => {
    navigate('/login'); // Redirect to login page   
  } 

 return (
  <div className="min-h-screen flex bg-[#E5E1DA]">
    <Sidebar onLogout={handleLogout} />
    <main
      className="flex-1 p-2 md:p-8 overflow-hidden ml-0 md:ml-16 transition-all duration-300"
    >
      <div className="flex flex-col h-full">
        <div className="flex flex-col md:flex-row justify-center sm:justify-between items-center text-center md:items-center mb-4 md:mb-6 gap-4">
          <h1 className="text-2xl md:text-3xl font-bold text-[#2b6777]">Currency Master</h1>
          <button
            className="flex items-center gap-2 bg-[#92C7CF] hover:bg-[#AAD7D9] text-white font-semibold px-4 py-2 rounded-lg shadow transition w-full md:w-auto justify-center"
            onClick={handleCreate}
          >
            <FaPlus /> Create New Currency
          </button>
        </div>
        
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-auto overflow-x-auto scroll-smooth">
            <table className="w-full bg-white rounded-2xl shadow-lg border-separate border-spacing-0 min-w-[600px]">
              <thead>
                <tr>
                  <th 
                    className={`sticky top-0 z-20 bg-[#AAD7D9] w-auto text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold rounded-tl-2xl cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                    onClick={() => handleSort('CurrencyCode')}
                  >
                    <div className="flex items-center justify-center">
                      Currency Code {getSortIcon('CurrencyCode')}
                    </div>
                  </th>
                  <th 
                    className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                    onClick={() => handleSort('CurrencyName')}
                  >
                    <div className="flex items-center justify-center">
                      Currency Name {getSortIcon('CurrencyName')}
                    </div>
                  </th>
                  <th 
                    className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                    onClick={() => handleSort('Symbol')}
                  >
                    <div className="flex items-center justify-center">
                      Symbol {getSortIcon('Symbol')}
                    </div>
                  </th>
                  <th 
                    className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                    onClick={() => handleSort('DefaultRate')}
                  >
                    <div className="flex items-center justify-center">
                      Default Rate {getSortIcon('DefaultRate')}
                    </div>
                  </th>
                  <th 
                    className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                    onClick={() => handleSort('Status')}
                  >
                    <div className="flex items-center justify-center">
                      Status {getSortIcon('Status')}
                    </div>
                  </th>
                  <th 
                    className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                    onClick={() => handleSort('EffectiveDate')}
                  >
                    <div className="flex items-center justify-center">
                      Effective Date {getSortIcon('EffectiveDate')}
                    </div>
                  </th>
                  <th 
                    className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                    onClick={() => handleSort('LastUpdated')}
                  >
                    <div className="flex items-center justify-center">
                      Last Updated {getSortIcon('LastUpdated')}
                    </div>
                  </th>
                  <th className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-bold rounded-tr-2xl transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-[#AAD7D9] font-semibold">
                      Loading currencies...
                    </td>
                  </tr>
                ) : currencies.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-[#AAD7D9] font-semibold">
                      No currencies found.
                    </td>
                  </tr>
                ) : currencies.map((currency, idx) => (
                  <tr
                    key={currency.CurrencyCode}
                    className={`transition-all duration-300 group ${getStatusText(currency.Status) === 'Active' ? 'hover:bg-[#eaf6f6] hover:shadow-md' : 'opacity-60'} border-b last:border-b-0 transform hover:scale-[1.01]`}
                    style={{ boxShadow: idx === currencies.length - 1 ? '0 8px 24px 0 rgba(44,103,119,0.04)' : undefined }}
                  >
                    <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-mono whitespace-nowrap flex justify-center items-center">
                      <p className='cursor-pointer transition-all duration-200 hover:scale-105' style={{ color: 'blue', borderBottom: '1px solid blue', width: 'fit-content' }} onClick={() => handleView(currency)}>{currency.CurrencyCode}</p>
                    </td>
                    <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap font-semibold">{currency.CurrencyName}</td>
                    <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap font-bold text-lg">{currency.Symbol}</td>
                    <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap">{currency.DefaultRate}</td>
                    <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm">
                      {console.log('Currency Status:', currency.CurrencyCode, currency.Status)}
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm transition-all duration-200
                        ${getStatusColor(currency.Status)}`}>
                        {getStatusText(currency.Status)}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-700">
                      {currency.EffectiveDate ? (
                        <>
                          {dayjs(currency.EffectiveDate).format('YYYY-MM-DD')}
                        </>
                      ) : '-'}
                    </td>
                    <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-700">
                      {currency.LastUpdated ? (
                        <>
                          {dayjs(currency.LastUpdated).format('YYYY-MM-DD')}
                          <br />
                          <span className="text-xs text-gray-400">{dayjs(currency.LastUpdated).format('HH:mm')}</span>
                        </>
                      ) : '-'}
                    </td>
                    <td className="px-4 md:px-6 py-3 md:py-4 flex gap-2 justify-center items-center">
                      <button title="View" className="p-1 md:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleView(currency)}>
                        <FaEye className="text-sm md:text-base" />
                      </button>
                      <button title="Edit" className="p-1 md:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleEdit(currency)}>
                        <FaEdit className="text-sm md:text-base" />
                      </button>
                      {getStatusText(currency.Status) === 'Active' ? (
                        <button
                          title="Deactivate"
                          className="p-1 md:p-2 rounded-full hover:bg-red-100 text-red-500 transition-all duration-200 hover:scale-110"
                          onClick={() => handleDeactivate(currency)}
                        >
                          <FaBan className="text-sm md:text-base" />
                        </button>
                      ) : (
                        <button
                          title="Reactivate"
                          className="p-1 md:p-2 rounded-full hover:bg-green-100 text-green-500 transition-all duration-200 hover:scale-110"
                          onClick={() => handleReactivate(currency)}
                        >
                          <FaCheck className="text-sm md:text-base" />
                        </button>
                      )}
                      <button title="Download CSV" className="p-1 md:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={handleDownloadCSV}>
                        <FaDownload className="text-sm md:text-base" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <CurrencyFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedCurrency}
          isEdit={isEdit}
          loading={formLoading}
        />
        <CurrencyViewModal
          open={showView}
          onClose={() => setShowView(false)}
          currency={selectedCurrency}
        />
      </div>
    </main>
  </div>
);
};

export default CurrencyMaster; 